import React from 'react';
import { Box, Button, Chip, Divider, Grid, styled, Typography } from '@mui/material';
import { IAction } from '../models/Action';
import { mapActionTypeToTimelineTitle } from '../utils/actionTypeToTitle';
import { ActionDetailModal } from 'components/actions/modals/ActionDetailModal';
import { getActionDate } from '../utils/formatDate';

interface ActionCardBaseProps {
    action: IAction;
    broken?: boolean;
    size?: 'single' | 'double';
    buttons?: React.ReactElement;
    showDate?: boolean;
    children?: React.ReactNode;
}

const pointsSize: Record<'single' | 'double', number> = {
    single: 42,
    double: 71
};

const PREFIX = 'ActionCardBase';

const classes = {
    root: `${PREFIX}-root`
};

const StyledGrid = styled(Grid)(({ theme }) => ({
    [`&.${classes.root}`]: ({ size = 'single' }: ActionCardBaseProps) => ({
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            width: theme.spacing(pointsSize[size])
        }
    })
}));

export const ActionCardBase: React.FC<ActionCardBaseProps> = props => {
    const { action, children, broken, buttons, showDate, size: _size, ...rest } = props;
    const [isActionDetailOpen, setActionDetailOpen] = React.useState(false);

    const toggleActionDetailClick = React.useCallback(() => {
        setActionDetailOpen(prevValue => !prevValue);
    }, []);

    return (
        <StyledGrid container spacing={1} className={classes.root} {...rest}>
            <Grid container spacing={1} alignItems="center" size={12}>
                <Grid size={showDate ? 8 : 12}>
                    <Typography variant="h6">{mapActionTypeToTimelineTitle(action)}</Typography>
                </Grid>
                {showDate && (
                    <Grid style={{ textAlign: 'right' }} size={4}>
                        <Chip label={getActionDate(action)} style={{ lineHeight: 'normal' }} />
                    </Grid>
                )}
            </Grid>
            <Grid size={12}>
                <Divider />
            </Grid>
            {broken ? (
                <Typography>Sorry, the details of this event can&apos;t be loaded at the moment</Typography>
            ) : (
                <>
                    <Grid size={12}>{children}</Grid>
                    <Grid size={12}>
                        <Box display="flex" justifyContent="flex-end" marginTop={2}>
                            <Box marginRight={2}>
                                <Button onClick={toggleActionDetailClick} variant="outlined" color="primary">
                                    View Detail
                                </Button>
                            </Box>
                            {!!buttons && buttons}
                        </Box>
                    </Grid>
                </>
            )}
            <ActionDetailModal action={action} open={isActionDetailOpen} onClose={toggleActionDetailClick} />
        </StyledGrid>
    );
};
